import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { Loader } from "@package/subframe/components/Loader";
import {
  FeatherArrowLeft,
  FeatherCalendar,
  FeatherCheckCircle,
  FeatherClock,
  FeatherEdit2,
  FeatherPaperclip,
  FeatherRotateCcw,
  FeatherShield,
  FeatherTrash2,
  FeatherUpload,
} from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { useAuth } from "@/contexts/AuthContext";
import { useListCategoriesQueryControllerList } from "@/generated/categories/categories";
import {
  useGetTaskDefinitionByIdQueryControllerGet,
  useListTaskInstancesByNurseryQueryControllerList,
} from "@/generated/compliance-tasks/compliance-tasks";
import { generateUploadUrlControllerGenerateUploadUrl } from "@/generated/files/files";
import {
  type ComplianceTaskDefinitionResponseDto,
  type ComplianceTaskInstanceResponseDto,
  ComplianceTaskInstanceResponseDtoStatus as InstanceStatus,
  GenerateUploadUrlDtoAssociatedEntityType,
} from "@/generated/model";
import {
  extractApiErrorMessage,
  invalidateComplianceQueries,
} from "@/lib/compliance/invalidate-compliance-queries";
import { NURSERY_ROLE_LABELS } from "@/lib/roles";

import { CompletionRecordDetails } from "./completion-record/completion-record";
import {
  formatCompleterName,
  formatRecordDate as formatDate,
  formatRecordDateTime as formatDateTime,
  hasCompletionRecordDetail,
} from "./completion-record/completion-record-view";
import { sortCategoryIdsByCatalog } from "./compliance-constants";
import { DeleteInstanceDialog } from "./DeleteInstanceDialog";
import { RemoveEvidenceDialog } from "./RemoveEvidenceDialog";
import { ReopenInstanceDialog } from "./ReopenInstanceDialog";
import { RescheduleInstanceDialog } from "./RescheduleInstanceDialog";

const OPEN_STATUSES: InstanceStatus[] = [
  InstanceStatus.NOT_YET_DUE,
  InstanceStatus.DUE_SOON,
  InstanceStatus.OVERDUE,
  InstanceStatus.AWAITING_EVIDENCE,
];

function getStatusBadge(status: InstanceStatus) {
  switch (status) {
    case InstanceStatus.OVERDUE:
      return <Badge variant="error">Overdue</Badge>;
    case InstanceStatus.DUE_SOON:
      return <Badge variant="warning">Due soon</Badge>;
    case InstanceStatus.AWAITING_EVIDENCE:
      return (
        <Badge variant="warning" icon={<FeatherPaperclip />}>
          Awaiting certificate
        </Badge>
      );
    case InstanceStatus.COMPLETED:
      return <Badge variant="success">Completed</Badge>;
    case InstanceStatus.CANCELLED:
      return <Badge variant="neutral">Cancelled</Badge>;
    case InstanceStatus.NOT_YET_DUE:
    default:
      return <Badge variant="neutral">Open</Badge>;
  }
}

interface Props {
  definitionId: string;
}

const RESCHEDULABLE_STATUSES: InstanceStatus[] = [
  InstanceStatus.NOT_YET_DUE,
  InstanceStatus.DUE_SOON,
  InstanceStatus.OVERDUE,
];

export function ComplianceDefinitionHistoryPage({ definitionId }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { userRoleType, isPlatformAdmin } = useAuth();

  // Same rule as the task list: only group (organisation) admins get the
  // correction affordances (reschedule / delete / evidence management).
  const canManage = isPlatformAdmin || userRoleType === "ORG_ADMIN";

  const [rescheduleTarget, setRescheduleTarget] =
    useState<ComplianceTaskInstanceResponseDto | null>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<ComplianceTaskInstanceResponseDto | null>(null);
  const [reopenTarget, setReopenTarget] =
    useState<ComplianceTaskInstanceResponseDto | null>(null);
  const [removeFileTarget, setRemoveFileTarget] = useState<{
    instanceId: string;
    fileId: string;
    fileName: string;
  } | null>(null);

  const handleUploadFile = async (instanceId: string, file: File) => {
    try {
      const response = await generateUploadUrlControllerGenerateUploadUrl({
        fileName: file.name,
        mimeType: file.type,
        size: file.size,
        associatedEntityType:
          GenerateUploadUrlDtoAssociatedEntityType.COMPLIANCE_TASK_INSTANCE,
        associatedEntityId: instanceId,
      });
      const { fileId, uploadUrl } = response.data;
      if (!fileId || !uploadUrl) {
        throw new Error("Invalid response from generate upload URL");
      }
      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!uploadResponse.ok) {
        throw new Error("Upload failed");
      }
      toast.success(
        "Certificate uploaded — it can take a moment to appear in the list",
      );
      await invalidateComplianceQueries(queryClient);
    } catch (error: unknown) {
      toast.error(
        extractApiErrorMessage(error, "Failed to upload the certificate"),
      );
    }
  };

  const { data: definitionResponse, isLoading: definitionLoading } =
    useGetTaskDefinitionByIdQueryControllerGet(definitionId);

  const definition = definitionResponse?.data;
  const nurseryId = definition?.nurseryId;

  const { data: categoriesResponse } = useListCategoriesQueryControllerList();
  const categories = categoriesResponse?.data.items ?? [];
  const categoryNameById = new Map(categories.map((c) => [c.id, c.name]));

  // Fetch ALL instances for this definition with completer-name enrichment.
  // Server-side filter on `taskDefinitionId` keeps the response tight; the
  // `includeCompleterNames` flag adds firstName/lastName/email so the history
  // rows show "Completed by Jane Smith" rather than a bare userId.
  // `includeChecklistResults` opts back into per-item results, which the
  // expandable detail renders (the list endpoint omits them by default).
  const { data: instancesResponse, isLoading: instancesLoading } =
    useListTaskInstancesByNurseryQueryControllerList(
      nurseryId ?? "",
      {
        taskDefinitionId: definitionId,
        includeCompleterNames: true,
        includeFiles: true,
        includeChecklistResults: true,
        limit: 200,
      },
      { query: { enabled: !!nurseryId, refetchOnMount: "always" } },
    );

  const instances = useMemo<ComplianceTaskInstanceResponseDto[]>(() => {
    const body = instancesResponse?.data as
      | {
          data?: ComplianceTaskInstanceResponseDto[];
          items?: ComplianceTaskInstanceResponseDto[];
        }
      | ComplianceTaskInstanceResponseDto[]
      | undefined;
    if (!body) return [];
    if (Array.isArray(body)) return body;
    return body.data ?? body.items ?? [];
  }, [instancesResponse]);

  const { openInstance, history } = useMemo(() => {
    const open = instances.find((i) => OPEN_STATUSES.includes(i.status));
    // History = everything that's NOT the current open instance, sorted most
    // recent first (by completedAt or dueDate as a fallback so cancelled
    // instances without a completedAt still slot in roughly correctly).
    const hist = instances
      .filter((i) => !open || i.id !== open.id)
      .sort((a, b) => {
        const ta = a.completedAt
          ? new Date(a.completedAt).getTime()
          : new Date(a.dueDate).getTime();
        const tb = b.completedAt
          ? new Date(b.completedAt).getTime()
          : new Date(b.dueDate).getTime();
        return tb - ta;
      });
    return { openInstance: open, history: hist };
  }, [instances]);

  // Only the most-recently-completed instance is reopenable (the backend rejects
  // reopening mid-chain history), so the affordance is shown on that row alone.
  const mostRecentCompletedId = useMemo(() => {
    const completed = instances.filter(
      (i) => i.status === InstanceStatus.COMPLETED,
    );
    if (completed.length === 0) return null;
    return completed.reduce((latest, i) =>
      new Date(i.dueDate).getTime() > new Date(latest.dueDate).getTime()
        ? i
        : latest,
    ).id;
  }, [instances]);

  if (definitionLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center py-12">
        <Loader size="large" />
      </div>
    );
  }

  if (!definition) {
    return (
      <div className="flex h-full w-full items-center justify-center py-12">
        <span className="text-body font-body text-subtext-color">
          Compliance task not found.
        </span>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col gap-6 bg-white overflow-y-auto p-6">
      {/* Header */}
      <div className="flex w-full items-start gap-3">
        <IconButton
          variant="neutral-secondary"
          icon={<FeatherArrowLeft />}
          aria-label="Back"
          onClick={() =>
            navigate({
              to: "/dashboard",
              search: { tab: "compliance" },
            })
          }
        />
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2">
          <span className="text-metrics font-metrics text-primary-navy-800">
            {definition.name}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="neutral" icon={<FeatherShield />}>
              Compliance
            </Badge>
            {definition.primaryCategoryId && (
              <Badge variant="brand">
                {categoryNameById.get(definition.primaryCategoryId) ??
                  "Unknown"}
              </Badge>
            )}
            {sortCategoryIdsByCatalog(
              definition.categoryIds.filter(
                (id) => id !== definition.primaryCategoryId,
              ),
              categories,
            ).map((id) => (
              <Badge key={id} variant="neutral">
                {categoryNameById.get(id) ?? "Unknown"}
              </Badge>
            ))}
            {definition.assigneeRole ? (
              <Badge variant="neutral">
                {NURSERY_ROLE_LABELS[definition.assigneeRole] ??
                  definition.assigneeRole}
              </Badge>
            ) : definition.assigneeUserId ? (
              <Badge variant="neutral">Assigned to a specific user</Badge>
            ) : null}
            {!definition.isActive && <Badge variant="warning">Inactive</Badge>}
          </div>
        </div>
      </div>

      {/* At-a-glance card */}
      <div className="grid w-full grid-cols-1 gap-3 md:grid-cols-3">
        <AtAGlanceCell
          icon={<FeatherCheckCircle className="h-4 w-4" />}
          label="Last completed"
          value={
            openInstance && OPEN_STATUSES.includes(openInstance.status)
              ? // The latest instance is still open — show the most recent
                // completed instance's completedAt (or "—" if there's never
                // been a completion).
                (history
                  .filter((i) => i.status === InstanceStatus.COMPLETED)
                  .map((i) => formatDate(i.completedAt))[0] ?? "—")
              : (history
                  .filter((i) => i.status === InstanceStatus.COMPLETED)
                  .map((i) => formatDate(i.completedAt))[0] ?? "—")
          }
        />
        <AtAGlanceCell
          icon={<FeatherCalendar className="h-4 w-4" />}
          label="Next due"
          valueNode={
            <div className="flex items-center gap-2">
              <span
                className={`text-body-bold font-body-bold ${
                  openInstance ? "text-default-font" : "text-subtext-color"
                }`}
              >
                {openInstance
                  ? (formatDate(openInstance.dueDate) ?? "—")
                  : "Series ended"}
              </span>
              {canManage && openInstance && (
                <div className="flex items-center gap-1">
                  {RESCHEDULABLE_STATUSES.includes(openInstance.status) && (
                    <IconButton
                      size="small"
                      variant="neutral-tertiary"
                      icon={<FeatherEdit2 />}
                      onClick={() => setRescheduleTarget(openInstance)}
                      aria-label="Change due date"
                    />
                  )}
                  <IconButton
                    size="small"
                    variant="neutral-tertiary"
                    icon={<FeatherTrash2 />}
                    onClick={() => setDeleteTarget(openInstance)}
                    aria-label="Delete this instance"
                  />
                </div>
              )}
            </div>
          }
        />
        <AtAGlanceCell
          icon={<FeatherClock className="h-4 w-4" />}
          label="Current status"
          valueNode={
            openInstance ? (
              getStatusBadge(openInstance.status)
            ) : (
              <Badge variant="neutral">No open instance</Badge>
            )
          }
        />
      </div>

      {/* Description */}
      {definition.description && (
        <div className="flex w-full flex-col items-start gap-1 rounded-lg border border-neutral-200 bg-neutral-50/40 px-4 py-3">
          <span className="text-caption-bold font-caption-bold text-subtext-color">
            Description
          </span>
          <span className="text-body font-body text-default-font whitespace-pre-wrap">
            {definition.description}
          </span>
        </div>
      )}

      {/* History section */}
      <div className="flex w-full flex-col gap-3">
        <span className="text-section-header font-section-header text-default-font">
          History
        </span>

        {instancesLoading ? (
          <div className="flex w-full items-center justify-center py-8">
            <Loader size="medium" />
          </div>
        ) : history.length === 0 ? (
          <div className="flex w-full items-center justify-center py-8">
            <span className="text-body font-body text-subtext-color">
              No completion history yet.
            </span>
          </div>
        ) : (
          <div className="flex w-full flex-col gap-2">
            {history.map((instance) => (
              <HistoryRow
                key={instance.id}
                instance={instance}
                definition={definition}
                canManage={canManage}
                canReopen={
                  canManage &&
                  definition.isActive &&
                  instance.id === mostRecentCompletedId
                }
                onReopen={() => setReopenTarget(instance)}
                onDelete={() => setDeleteTarget(instance)}
                onRemoveFile={(instanceId, fileId, fileName) =>
                  setRemoveFileTarget({ instanceId, fileId, fileName })
                }
                onUploadFile={handleUploadFile}
              />
            ))}
          </div>
        )}
      </div>

      {rescheduleTarget && (
        <RescheduleInstanceDialog
          open={!!rescheduleTarget}
          onOpenChange={(open) => !open && setRescheduleTarget(null)}
          instance={rescheduleTarget}
        />
      )}
      {deleteTarget && (
        <DeleteInstanceDialog
          open={!!deleteTarget}
          onOpenChange={(open) => !open && setDeleteTarget(null)}
          instance={deleteTarget}
          isOnlyOpenInstance={deleteTarget.id === openInstance?.id}
        />
      )}
      {reopenTarget && (
        <ReopenInstanceDialog
          open={!!reopenTarget}
          onOpenChange={(open) => !open && setReopenTarget(null)}
          instance={reopenTarget}
        />
      )}
      {removeFileTarget && (
        <RemoveEvidenceDialog
          open={!!removeFileTarget}
          onOpenChange={(open) => !open && setRemoveFileTarget(null)}
          instanceId={removeFileTarget.instanceId}
          fileId={removeFileTarget.fileId}
          fileName={removeFileTarget.fileName}
        />
      )}
    </div>
  );
}

interface AtAGlanceCellProps {
  icon: React.ReactNode;
  label: string;
  value?: string;
  valueNode?: React.ReactNode;
  muted?: boolean;
}

function AtAGlanceCell({
  icon,
  label,
  value,
  valueNode,
  muted,
}: AtAGlanceCellProps) {
  return (
    <div className="flex flex-col items-start gap-1 rounded-lg border border-neutral-200 px-4 py-3">
      <div className="flex items-center gap-1.5 text-subtext-color">
        {icon}
        <span className="text-caption font-caption">{label}</span>
      </div>
      {valueNode ?? (
        <span
          className={`text-body-bold font-body-bold ${muted ? "text-subtext-color" : "text-default-font"}`}
        >
          {value}
        </span>
      )}
    </div>
  );
}

function HistoryRow({
  instance,
  definition,
  canManage = false,
  canReopen = false,
  onReopen,
  onDelete,
  onRemoveFile,
  onUploadFile,
}: {
  instance: ComplianceTaskInstanceResponseDto;
  definition: ComplianceTaskDefinitionResponseDto;
  canManage?: boolean;
  canReopen?: boolean;
  onReopen?: () => void;
  onDelete?: () => void;
  onRemoveFile?: (instanceId: string, fileId: string, fileName: string) => void;
  onUploadFile?: (instanceId: string, file: File) => Promise<void>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const completer = instance.completedBy;
  // Admins can always expand: the detail view is where evidence is managed,
  // even on rows with nothing else to show.
  const hasDetail =
    hasCompletionRecordDetail(instance, definition) || canManage;

  const handleUploadSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onUploadFile) return;
    setUploading(true);
    try {
      await onUploadFile(instance.id, file);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="flex w-full flex-col rounded-lg border border-neutral-200">
      <div className="flex w-full items-center gap-1">
        <button
          type="button"
          onClick={() => hasDetail && setExpanded(!expanded)}
          className={`flex grow shrink-0 basis-0 items-center justify-between px-4 py-3 text-left transition-colors ${
            hasDetail ? "cursor-pointer hover:bg-neutral-50" : "cursor-default"
          }`}
        >
          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-body-bold font-body-bold text-default-font">
                Due {formatDate(instance.dueDate) ?? "—"}
              </span>
              {getStatusBadge(instance.status)}
            </div>
            {instance.status === InstanceStatus.COMPLETED &&
              instance.completedAt && (
                <span className="text-caption font-caption text-subtext-color">
                  Completed {formatDateTime(instance.completedAt) ?? "—"} by{" "}
                  {formatCompleterName(completer) ?? "Unknown user"}
                </span>
              )}
            {instance.status === InstanceStatus.AWAITING_EVIDENCE && (
              <span className="text-caption font-caption text-subtext-color">
                Submitted — certificate pending
              </span>
            )}
            {instance.status === InstanceStatus.CANCELLED && (
              <span className="text-caption font-caption text-subtext-color">
                Cancelled
              </span>
            )}
          </div>
          {hasDetail && (
            <span className="text-caption font-caption text-subtext-color">
              {expanded ? "Hide details" : "Show details"}
            </span>
          )}
        </button>
        {canReopen &&
          onReopen &&
          instance.status === InstanceStatus.COMPLETED && (
            <IconButton
              className="flex-none"
              size="small"
              variant="neutral-tertiary"
              icon={<FeatherRotateCcw />}
              onClick={onReopen}
              aria-label="Reopen this instance"
            />
          )}
        {canManage && onDelete && (
          <IconButton
            className="mr-3 flex-none"
            size="small"
            variant="neutral-tertiary"
            icon={<FeatherTrash2 />}
            onClick={onDelete}
            aria-label="Delete this instance"
          />
        )}
      </div>

      {expanded && hasDetail && (
        <div className="flex w-full flex-col gap-3 border-t border-neutral-200 px-4 py-3">
          <CompletionRecordDetails
            instance={instance}
            definition={definition}
            fileActions={(file) =>
              canManage && onRemoveFile ? (
                <IconButton
                  className="mr-2 flex-none"
                  size="small"
                  variant="neutral-tertiary"
                  icon={<FeatherTrash2 />}
                  onClick={() =>
                    onRemoveFile(instance.id, file.id, file.fileName)
                  }
                  aria-label={`Remove ${file.fileName}`}
                />
              ) : null
            }
            footer={
              canManage && onUploadFile ? (
                <div className="flex w-full flex-col items-start gap-2">
                  <input
                    ref={uploadInputRef}
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={handleUploadSelect}
                  />
                  <Button
                    variant="neutral-secondary"
                    size="small"
                    icon={<FeatherUpload />}
                    onClick={() => uploadInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? "Uploading…" : "Upload certificate"}
                  </Button>
                </div>
              ) : null
            }
          />
        </div>
      )}
    </div>
  );
}
